import * as React from 'react';

import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { Button, TextField } from '@rematter/ui-web';
import {
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogOverlay,
  DialogMain,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import { TaskDefinitionEditor } from './TaskDefinitionEditor';

import { useUpdateServiceTypeMutation } from '~/modules/transportation/hooks/mutations/useUpdateServiceTypeMutation';
import {
  $resetServiceTypeForm,
  $serviceTypeForm,
  $updateServiceTypeForm,
} from '~/modules/transportation/state/serviceTypeForm.localState';
import { ServiceTypeRecord } from '~/modules/transportation/types';

type ServiceTypeDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  serviceType?: ServiceTypeRecord;
};

export const ServiceTypeDialog = ({
  isOpen,
  onClose,
  onSuccess,
  serviceType,
}: ServiceTypeDialogProps) => {
  const t = useTranslations();
  const [isEditMode, setIsEditMode] = React.useState(false);
  const form = useAtomValue($serviceTypeForm);
  const resetForm = useSetAtom($resetServiceTypeForm);
  const updateForm = useSetAtom($updateServiceTypeForm);
  const toast = useToast();

  const { mutateAsync: updateServiceType, isPending: isUpdating } =
    useUpdateServiceTypeMutation();

  React.useEffect(() => {
    if (serviceType) {
      updateForm({
        name: serviceType.name,
        description: serviceType.description,
        isActive: serviceType.isActive,
        isSystemDefault: serviceType.isSystemDefault,
        taskDefinitions: [],
      });
    }
    setIsEditMode(false);
  }, [serviceType, updateForm]);

  const handleClose = () => {
    onClose();
    setIsEditMode(false);
    resetForm();
  };

  const handleSave = async () => {
    if (!serviceType?.id) return;

    try {
      await updateServiceType({
        id: serviceType.id,
        name: form.name,
      });
      toast.success(t.serviceTypes.messages.updateSuccess);
      setIsEditMode(false);
      onSuccess();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t.serviceTypes.messages.error,
      );
    }
  };

  if (!serviceType) return null;

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={handleClose}
      isDismissable={false}
      className="@ServiceTypeDialog"
    >
      <DialogContent position="right">
        <DialogHeader slot="title">
          <div className="flex flex-col gap-2">
            <div className="text-xl font-semibold">
              {isEditMode
                ? t.serviceTypes.dialog.title.edit
                : t.serviceTypes.dialog.title.view}
            </div>
          </div>
        </DialogHeader>

        <DialogMain>
          <div className="space-y-4">
            <TextField
              label={t.serviceTypes.dialog.fields.name}
              value={form.name}
              onChange={(value: string) => updateForm({ name: value })}
              isRequired
              isDisabled={!isEditMode}
            />
            <TextField
              label={t.serviceTypes.dialog.fields.description}
              value={form.description ?? ''}
              isDisabled
            />

            <TaskDefinitionEditor serviceTypeId={serviceType.id} />
          </div>
        </DialogMain>

        <DialogFooter>
          {isEditMode ? (
            <>
              <Button
                color="failure"
                variant="outline"
                isPending={isUpdating}
                onPress={() => {
                  updateForm({ name: serviceType.name });
                  setIsEditMode(false);
                }}
              >
                {t.serviceTypes.dialog.buttons.cancel}
              </Button>
              <Button
                color="primary"
                variant="solid"
                onPress={handleSave}
                isPending={isUpdating}
                isLoading={isUpdating}
              >
                {t.serviceTypes.dialog.buttons.save}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onPress={handleClose}>
                {t.serviceTypes.dialog.buttons.close}
              </Button>
              <Button
                color="primary"
                variant="solid"
                onPress={() => setIsEditMode(true)}
              >
                {t.serviceTypes.dialog.title.edit}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
